import { snakeCase } from 'lodash';
import * as Sentry from '@sentry/browser';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';

import { fetchCurrentAccount } from '../../access/reducers/account';
import { organizationsListUpdated } from 'modules/organizations/reducers/organizations';
import { receiveInfluencerImages, resetInfluencerImages } from './influencerImages';
import { trackEventProfileCreated, trackEventProfileUpdated } from '../../tracking/utils/tracking';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post, put } from 'utils/api';
import { t } from 'utils/language';
import { getMissingImagesValidationError } from 'utils/profile';
import { logError } from 'utils/log';
import { getAllInfluecerAssetsData, getTransformedChannels } from 'utils/influencers';

import type { InfluencerProfileForm } from 'types';

const RECEIVE = 'influencerProfile/RECEIVE';
const RESET = 'influencerProfile/RESET';
const LOADING = 'influencerProfile/LOADING';

const {
  createInfluencerAudienceData,
  deleteInfluencerProfile,
  getInfluencerProfile,
  getInfluencersAudienceData,
  createInfluencerProfile: createInfluencerProfileEndpoint,
  updateInfluencerProfile
} = API.END_POINTS.INFLUENCERS;

const initialState = {
  profile: {},
  isLoading: null
};

function receiveInfluencerProfile(data) {
  return {
    type: RECEIVE,
    payload: {
      data
    }
  };
}

function loadingInfluencerProfile(isLoading: boolean) {
  return {
    type: LOADING,
    payload: {
      isLoading
    }
  };
}

export function resetInfluencerProfile() {
  return (dispatch) => {
    dispatch({
      type: RESET
    });
  };
}

export function removeInfluencerProfile(profileId, reason) {
  return dispatch => {
    const url = deleteInfluencerProfile(profileId, reason);

    dispatch(showLoading(url));

    return destroy(url, {}, {
      suppressGlobalNotification: true
    })
      .catch(error => {
        const code = snakeCase(error?.response?.data?.code ?? '') || null;
        const message = t(`delete_influencer_profile.${code}`) || t('delete_influencer_profile.common');

        dispatch(showConfirmModal({
          message,
          confirmButtonLabel: t('action_button_ok'),
          onConfirm: () => dispatch(hideConfirmModal())
        }));

        throw error;
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function saveInfluencerAudienceData(profileId, assets) {
  return () => {
    const audienceDataUrl = createInfluencerAudienceData(profileId);

    return put(audienceDataUrl, {
      data: assets
    });
  };
}

function sendInfluencerProfile(url, method, profileDetails, isLightweight) {
  const requestMethod = method === 'create' ? post : put;
  const trackingMethod = method === 'create' ? trackEventProfileCreated : trackEventProfileUpdated;

  return (dispatch, getState) => {
    const influencerImages = getState().influencerImages;

    const missingImagesError = getMissingImagesValidationError(
      influencerImages.profileImage
    );

    if (missingImagesError && !isLightweight) {
      dispatch(danger(missingImagesError));
      return Promise.reject(missingImagesError);
    }

    dispatch(showLoading(url));

    const profileDetailsWithImages = {
      ...profileDetails,
      profileImage: influencerImages.profileImage
    };

    const assets = getAllInfluecerAssetsData(profileDetails.channels);

    return requestMethod(url, { data: profileDetailsWithImages })
      .then((response) => {
        const influencerProfile = response.data;
        dispatch(hideLoading(url));
        if (method === 'update') {
          dispatch(success(t('save_influencer_profile_success'), { keepBetweenPages: true }));
        }
        trackingMethod('influencer');

        return dispatch(saveInfluencerAudienceData(influencerProfile.id, assets))
          .catch(error => logError(error))
          .then(() =>
            dispatch(fetchCurrentAccount()) // it will load updated currentOrganization.influencerProfiles
              .then(account => dispatch(organizationsListUpdated(account.organizations)))
              .then(() => influencerProfile)
              .catch(error => logError(error))
          );
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        if (error.response) {
          dispatch(danger(t('save_influencer_profile_error'), { keepBetweenPages: true }));
        }

        throw error;
      });
  };
}

export function createInfluencerProfile(profileDetails) {
  return (dispatch, getState) => {
    const organizationId = getState().organizations.currentOrganizationId;
    const url = createInfluencerProfileEndpoint(organizationId);

    return dispatch(sendInfluencerProfile(url, 'create', profileDetails, false));
  };
}

export function fetchInfluencerAudienceData(profileId: number) {
  return (dispatch) => {
    const url = getInfluencersAudienceData(profileId);

    dispatch(showLoading(url));

    return get(url)
      .then(response => response.data)
      .catch(error => {
        logError(error);

        return [];
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function saveInfluencerProfile(profileId: number, profileDetails: InfluencerProfileForm, isLightweight?: boolean) {
  return (dispatch) => {
    const url = updateInfluencerProfile(profileId);

    return dispatch(sendInfluencerProfile(url, 'update', profileDetails, isLightweight));
  };
}

export function fetchInfluencerProfile(profileId) {
  return (dispatch) => {
    const url = getInfluencerProfile(profileId);

    dispatch(showLoading(url));
    dispatch(resetInfluencerImages());

    dispatch(loadingInfluencerProfile(true));

    return get(url)
      .then((response) => {
        const influencerProfile = response.data;

        // Transform new 'metricsV2' to 'metrics' for proper flow in FE
        // metricsv2 and metrics are same with different structure
        influencerProfile.channels = getTransformedChannels(influencerProfile.channels);

        dispatch(receiveInfluencerProfile(influencerProfile));
        dispatch(receiveInfluencerImages(influencerProfile));

        return influencerProfile;
      })
      .catch((error) => {
        logError(error);

        dispatch(loadingInfluencerProfile(false));

        Sentry.captureException(error);
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export default createReducer<any, any>('influencerProfile', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { data }) => ({
    ...state,
    profile: data,
    isLoading: false
  }),
  [LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  })
});
